import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, Phone, ArrowLeft } from 'lucide-react';
import { SEO } from '../components/shared/SEO';
import { useTenant } from '../hooks/useTenant';
import { useLanguage } from '../hooks/useLanguage';

/**
 * NotFoundPage - Custom 404 error page
 *
 * Branded 404 page that helps users navigate back to the site
 * with helpful links and search functionality (optional).
 */
const NotFoundPage: React.FC = () => {
  const { config } = useTenant();
  const { t } = useLanguage();

  const quickLinks = [
    {
      to: '/',
      icon: <Home className="w-5 h-5" />,
      label: t('notFound.quickLinks.home.label'),
      description: t('notFound.quickLinks.home.description')
    },
    {
      to: '/ventas',
      icon: <Search className="w-5 h-5" />,
      label: t('notFound.quickLinks.sales.label'),
      description: t('notFound.quickLinks.sales.description')
    },
    {
      to: '/arriendos',
      icon: <Search className="w-5 h-5" />,
      label: t('notFound.quickLinks.rentals.label'),
      description: t('notFound.quickLinks.rentals.description')
    },
    {
      to: '/contacto',
      icon: <Phone className="w-5 h-5" />,
      label: t('notFound.quickLinks.contact.label'),
      description: t('notFound.quickLinks.contact.description')
    }
  ];

  return (
    <>
      <SEO
        title={t('notFound.seoTitle')}
        description={t('notFound.seoDescription')}
        canonicalPath="/404"
        noIndex={true}
      />

      <div className="min-h-[80vh] flex items-center justify-center px-4 py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl w-full text-center">
          {/* 404 Number - Large and eye-catching */}
          <div className="mb-8">
            <h1 className="text-9xl md:text-[12rem] font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-700 leading-none">
              404
            </h1>
          </div>

          {/* Main Message */}
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('notFound.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('notFound.description')} {t('notFound.suggestion')}
            </p>
          </div>

          {/* Go Back Button */}
          <div className="mb-12">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('notFound.goBack')}
            </button>
          </div>

          {/* Quick Links Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {quickLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-200 border border-gray-200 hover:border-primary-500 hover:-translate-y-1"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="mb-3 p-3 bg-primary-50 rounded-full text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                    {link.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
                    {link.label}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {link.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {/* Additional Help Section */}
          {config && (
            <div className="mt-12 pt-8 border-t border-gray-300">
              <p className="text-gray-700 mb-4">
                {t('notFound.helpMessage')}
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                {config.phone && (
                  <a
                    href={`tel:${config.phone}`}
                    className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <Phone className="w-4 h-4" />
                    {config.phone}
                  </a>
                )}
                {config.email && (
                  <a
                    href={`mailto:${config.email}`}
                    className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    {config.email}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotFoundPage;
